// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Icon from "@mui/material/Icon";

import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import TeacherAvatar from "components/TeacherAvatar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";
import api from "services/api";
import SoftInput from "components/SoftInput";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useTranslate } from "utils/i18n";
import { teacherDetailPath } from "utils/teacherRoutes";
import {
  parseListPayload,
  extractAttendanceSummary,
  totalPagesFromCount,
} from "utils/paginationStats";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

function distanceBetweenCoordinates(lat1, lon1, lat2, lon2) {
  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : NaN;
  };
  const a = n(lat1);
  const b = n(lon1);
  const c = n(lat2);
  const d = n(lon2);
  if ([a, b, c, d].some((x) => Number.isNaN(x))) return null;

  const earthRadius = 6371;
  const latFrom = (a * Math.PI) / 180;
  const lonFrom = (b * Math.PI) / 180;
  const latTo = (c * Math.PI) / 180;
  const lonTo = (d * Math.PI) / 180;
  const latDelta = latTo - latFrom;
  const lonDelta = lonTo - lonFrom;
  const angle =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin(latDelta / 2) ** 2 +
          Math.cos(latFrom) * Math.cos(latTo) * Math.sin(lonDelta / 2) ** 2
      )
    );
  return (angle * earthRadius).toFixed(2);
}

function rowDistanceKm(row) {
  const estLat = row?.estLatitude ?? row?.EstLatitude;
  const estLon = row?.estLongitude ?? row?.EstLongitude;
  const attLat = row?.attLatitude ?? row?.AttLatitude;
  const attLon = row?.attLongitude ?? row?.AttLongitude;
  const raw = distanceBetweenCoordinates(estLat, estLon, attLat, attLon);
  if (raw === null) return null;
  const km = parseFloat(raw);
  return Number.isFinite(km) ? km : null;
}

function parseAttendanceDate(raw) {
  if (raw === null || raw === undefined) return null;
  const d = new Date(String(raw).trim());
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Fallback when Date parsing fails — strip milliseconds for display */
function stripMillisDisplay(raw) {
  return String(raw ?? "")
    .trim()
    .replace("T", " ")
    .replace(/\.\d{3,}(?=\s|$)/g, "");
}

const homeStatWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(102, 126, 234, 0.07) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "rgba(102, 126, 234, 0.28)",
      boxShadow: "0 16px 42px rgba(102, 126, 234, 0.12)",
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const homeTableShellSx = {
  mx: { xs: 1.25, md: 2 },
  mb: 0,
  borderRadius: "14px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  overflow: "auto",
  maxHeight: { xs: "62vh", md: "min(68vh, 720px)" },
  bgcolor: figmaTokens.surface,
  boxShadow: "0 10px 40px rgba(15, 23, 42, 0.07)",
};

function homeTeacherStatusChipSx(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  const base = { fontWeight: 600, fontSize: "0.72rem", height: 26, borderRadius: "8px" };
  if (!s) {
    return {
      ...base,
      bgcolor: figmaTokens.pageBg,
      color: figmaTokens.textSecondary,
      border: `1px solid ${figmaTokens.borderSubtle}`,
    };
  }
  if (s.includes("inact") || s.includes("suspend") || s.includes("révoq") || s.includes("revoq")) {
    return { ...base, bgcolor: "rgba(239, 68, 68, 0.12)", color: "#b91c1c" };
  }
  if (s.includes("act") || s.includes("valid") || s === "ok" || s.includes("en poste")) {
    return { ...base, bgcolor: "rgba(102, 126, 234, 0.16)", color: figmaTokens.accent };
  }
  return { ...base, bgcolor: "rgba(100, 116, 139, 0.14)", color: figmaTokens.textSecondary };
}

const dialogPaperProps = {
  sx: {
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
  },
};

function Home() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filterDate, setFilterDate] = useState("");
  const [searchTick, setSearchTick] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [attendanceSummary, setAttendanceSummary] = useState({
    totalRecords: 0,
    thisMonthTotal: 0,
    thisWeekTotal: 0,
  });
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const { t } = useTranslate();

  const openTeacherProfile = (row) => {
    const id = row?.id ?? row?.Id;
    const reg = row?.regNumber ?? row?.RegNumber;
    const path = teacherDetailPath(id, reg);
    if (path) navigate(path);
  };

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        EstabId: "",
        pageNumber: page,
        pageSize,
      };
      if (filterDate) params.AttendDate = filterDate;

      const res = await api.get("/Teacher/scoresummary", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tk446655440000")}`,
        },
      });
      const list = parseListPayload(res.data);
      setTeachers(list);
      const summary = extractAttendanceSummary(list);
      setAttendanceSummary(summary);
      setTotalRecords(summary.totalRecords || list.length);
    } catch (err) {
      console.error("API Error:", err);
      setTeachers([]);
      setAttendanceSummary({ totalRecords: 0, thisMonthTotal: 0, thisWeekTotal: 0 });
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, filterDate, pageSize]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance, searchTick]);

  const formik = useFormik({
    initialValues: {
      date: "",
    },
    onSubmit: async (values) => {
      setFilterDate(values.date);
      setPage(1);
      setSearchTick((n) => n + 1);
    },
  });

  const totalPages = totalPagesFromCount(totalRecords, pageSize);

  const handlePageChange = (value) => {
    setPage(value);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const tableRangeLabel =
    totalRecords === 0
      ? null
      : t("home.table_range", "{{start}}–{{end}} sur {{total}} lignes")
          .replace("{{start}}", String((page - 1) * pageSize + 1))
          .replace("{{end}}", String(Math.min(page * pageSize, totalRecords)))
          .replace("{{total}}", String(totalRecords));

  const dateHint = formik.values.date
    ? t("home.date_filter_hint", "Date sélectionnée : {{date}}").replace("{{date}}", formik.values.date)
    : t("home.date_filter_all", "Toutes les dates (chargement initial)");

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }} className="content-area-main">
        <SoftBox mb={3}>
          <Card
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
              border: "none",
              boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)",
            }}
          >
            <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
            <SoftBox
              sx={{
                p: { xs: 2.25, md: 3 },
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "flex-start" },
                justifyContent: "space-between",
                gap: 2.5,
              }}
            >
              <SoftBox sx={{ minWidth: 0, flex: "1 1 280px" }}>
                <SoftTypography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    color: figmaTokens.textMuted,
                    fontSize: "0.68rem",
                  }}
                >
                  {t("home.page_kicker", "PRÉSENCES")}
                </SoftTypography>
                <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                  {t("home.title", "Liste des enseignants")}
                </SoftTypography>
                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
                  {t("home.page_hint", "Review check-ins for the selected date and open a teacher's attendance history.")}
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ display: "block", mt: 1.25, color: figmaTokens.textMuted, fontWeight: 600 }}>
                  {dateHint}
                </SoftTypography>
              </SoftBox>
              <SoftBox
                component="form"
                onSubmit={formik.handleSubmit}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                  gap: 1.5,
                  flex: "0 1 auto",
                }}
              >
                <SoftBox sx={{ minWidth: { xs: "100%", sm: 200 } }}>
                  <SoftTypography
                    component="label"
                    htmlFor="home-attend-date"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: figmaTokens.textMuted,
                    }}
                  >
                    {t("home.select_date", "Sélectionnez la date")}
                  </SoftTypography>
                  <SoftInput
                    id="home-attend-date"
                    type="date"
                    placeholder={t("home.date_placeholder", "Sélectionnez une date")}
                    name="date"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                  />
                </SoftBox>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "12px",
                    px: 2.75,
                    py: 1.1,
                    color: "#fff",
                    background: figmaTokens.gradientCSS,
                    boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                    "&:hover": {
                      filter: "brightness(1.06)",
                      boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                    },
                  }}
                >
                  {t("home.search", "Recherche")}
                </Button>
              </SoftBox>
            </SoftBox>
          </Card>
        </SoftBox>

        <SoftBox
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            mb: 3,
          }}
        >
          <SoftBox sx={homeStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("home.stats.records", "Check-ins") }}
              count={attendanceSummary.totalRecords}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "info", component: "fact_check" }}
            />
          </SoftBox>
          <SoftBox sx={homeStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("home.stats.this_month", "This month") }}
              count={attendanceSummary.thisMonthTotal}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "secondary", component: "calendar_month" }}
            />
          </SoftBox>
          <SoftBox sx={homeStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("home.stats.this_week", "This week") }}
              count={attendanceSummary.thisWeekTotal}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "dark", component: "date_range" }}
            />
          </SoftBox>
        </SoftBox>

        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
            bgcolor: figmaTokens.surface,
          }}
        >
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("home.table.section_title", "Attendance records")}
            </SoftTypography>
          </SoftBox>
          <SoftBox sx={{ px: { xs: 1.25, md: 2 }, pt: 2, pb: 2 }}>
            {tableRangeLabel && (
              <SoftTypography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: figmaTokens.textMuted,
                  textTransform: "uppercase",
                }}
              >
                {tableRangeLabel}
              </SoftTypography>
            )}
            <TableContainer component={Paper} elevation={0} sx={homeTableShellSx}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 1100,
                  ...directoryTableHeadStickyOverridesSx,
                  ...directoryTableBodyCellDefaultsSx,
                  "& .MuiTableRow-root:hover .MuiTableCell-root": {
                    bgcolor: "rgba(102, 126, 234, 0.045)",
                  },
                }}
                aria-label={t("home.title", "Liste des enseignants")}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 168 }}>
                      {t("home.table.attend_date", "Date de pointage")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("home.table.reg_number", "Matricule de l'enseignant")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 200 }}>
                      {t("home.table.teacher_name", "Nom et prénoms de l'enseignant")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.table.gender", "Sexe de l'enseignant")}</TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("home.table.status", "Statut de l'enseignant")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                      {t("home.table.degree", "Diplôme de l'enseignant")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, maxWidth: 200 }}>
                      {t("home.table.est_coords", "Coordonnées de l'établissement")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                      {t("home.table.att_coords", "Coordonnées du pointage")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 100 }}>
                      {t("home.table.distance", "Distance")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("home.table.imei", "IMEI du téléphone")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teachers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6, color: figmaTokens.textSecondary }}>
                        {t("home.table_empty", "Aucun pointage à afficher.")}
                      </TableCell>
                    </TableRow>
                  )}
                  {teachers.map((row) => {
                    const km = rowDistanceKm(row);
                    const statusRaw = row.status ?? row.Status ?? "";
                    const displayName = [row.name ?? row.Name, row.preName ?? row.PreName].filter(Boolean).join(" ") || "—";
                    return (
                      <TableRow
                        key={row.id ?? row.Id ?? `${row.regNumber}-${row.attendDateTime}`}
                        hover
                        sx={{
                          "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {row.attendDateTime?.replace("T", " ") ?? "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.regNumber ?? row.RegNumber ?? "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <SoftBox
                            component="button"
                            type="button"
                            onClick={() => openTeacherProfile(row)}
                            display="flex"
                            alignItems="center"
                            gap={1.25}
                            minWidth={0}
                            sx={{
                              border: "none",
                              background: "none",
                              cursor: "pointer",
                              p: 0,
                              m: 0,
                              maxWidth: "100%",
                              textAlign: "left",
                            }}
                          >
                            <TeacherAvatar teacher={row} size="xs" />
                            <SoftTypography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: figmaTokens.accent,
                                textDecoration: "underline",
                                textUnderlineOffset: 3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                "&:hover": { color: figmaTokens.accentEnd },
                              }}
                            >
                              {displayName}
                            </SoftTypography>
                          </SoftBox>
                        </TableCell>
                        <TableCell sx={{ color: figmaTokens.textBody }}>{row.gender ?? row.Gender ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={String(statusRaw || "--")}
                            size="small"
                            sx={homeTeacherStatusChipSx(statusRaw)}
                          />
                        </TableCell>
                        <TableCell sx={{ color: figmaTokens.textBody }}>{row.degree ?? row.Degree ?? "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 200, color: figmaTokens.textBody }}>
                          {row.estLatitude ?? row.EstLatitude} {row.estLongitude ?? row.EstLongitude}
                          <br />
                          <SoftTypography component="span" variant="caption" sx={{ color: figmaTokens.textMuted }}>
                            ({row.estName ?? row.EstName ?? "—"})
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          <Link
                            to="/map"
                            state={{ row }}
                            onClick={() => localStorage.setItem("mapData", JSON.stringify(row))}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: figmaTokens.accent,
                              fontWeight: 600,
                              textDecoration: "underline",
                            }}
                          >
                            {row.attLatitude ?? row.AttLatitude} {row.attLongitude ?? row.AttLongitude}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {km !== null ? `${km.toFixed(2)} km` : "—"}
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.78rem" }}>
                          {row.imei ?? row.Imei ?? "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationBar
              page={page}
              totalCount={totalRecords}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeLabel={t("pagination.page_size", "Rows per page")}
            />
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export function TeacherModal({ openModel, setOpenModel, teacherId }) {
  const [teacherHistory, setTeacherHistory] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;
  const { t } = useTranslate();

  const handleClose = () => {
    setOpenModel(false);
  };

  const fetchTeacherHistory = async () => {
    setLoading(true);
    setTeacherHistory(null);
    try {
      const res = await api.get(`/Teacher/history?Id=${teacherId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tk446655440000")}`,
        },
      });

      const historyData = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
      setTeacherHistory(historyData);
      setTeacherData(historyData.length > 0 ? historyData[0] : null);
    } catch (err) {
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teacherId) {
      fetchTeacherHistory();
    }
    if (openModel) {
      setPage(1);
    }
  }, [teacherId, openModel]);

  const modalStats = useMemo(() => {
    const list = Array.isArray(teacherHistory) ? teacherHistory : [];
    if (!list.length) {
      return {
        count: 0,
        avgKm: null,
        spanRangeShort: "—",
        spanTimeHint: "",
        farCount: 0,
      };
    }
    const distances = [];
    const times = [];
    for (const row of list) {
      const km = rowDistanceKm(row);
      if (km !== null) distances.push(km);
      const dt = row.attendDateTime ?? row.AttendDateTime;
      if (dt) times.push(String(dt));
    }
    times.sort();
    const avgKm =
      distances.length > 0 ? distances.reduce((a, b) => a + b, 0) / distances.length : null;

    let spanRangeShort = "—";
    let spanTimeHint = "";
    if (times.length > 0) {
      const firstD = parseAttendanceDate(times[0]);
      const lastD = parseAttendanceDate(times[times.length - 1]);
      const df = { dateStyle: "medium" };
      const tf = { hour: "2-digit", minute: "2-digit" };
      if (times.length === 1 && firstD) {
        spanRangeShort = firstD.toLocaleDateString(undefined, df);
        spanTimeHint = firstD.toLocaleTimeString(undefined, tf);
      } else if (firstD && lastD) {
        spanRangeShort = `${firstD.toLocaleDateString(undefined, df)} → ${lastD.toLocaleDateString(undefined, df)}`;
        spanTimeHint = `${firstD.toLocaleTimeString(undefined, tf)} – ${lastD.toLocaleTimeString(undefined, tf)}`;
      } else {
        spanRangeShort =
          times.length === 1
            ? stripMillisDisplay(times[0])
            : `${stripMillisDisplay(times[0])} → ${stripMillisDisplay(times[times.length - 1])}`;
      }
    }

    const farCount = distances.filter((d) => d > 1).length;
    return { count: list.length, avgKm, spanRangeShort, spanTimeHint, farCount };
  }, [teacherHistory]);

  const totalPages = teacherHistory ? Math.ceil(teacherHistory.length / rowsPerPage) : 0;
  const startIndex = (page - 1) * rowsPerPage;
  const currentTeacherHistory = teacherHistory?.slice(startIndex, startIndex + rowsPerPage) || [];

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const displayFirstName = teacherData?.name ?? teacherData?.Name ?? "";
  const displayLastName = teacherData?.preName ?? teacherData?.PreName ?? "";
  const displayReg = teacherData?.regNumber ?? teacherData?.RegNumber ?? "";

  return (
    <div>
      {loading && <Loading />}
      <Dialog open={openModel} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ p: 0 }}>
          <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
            <SoftTypography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: figmaTokens.textMuted,
                fontSize: "0.68rem",
              }}
            >
              {t("home.modal.kicker", "TEACHER")}
            </SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("home.modal.subtitle", "Attendance history")}
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" gap={1.5} mt={0.75}>
              <TeacherAvatar
                teacher={teacherData}
                name={displayFirstName}
                preName={displayLastName}
                size="sm"
              />
              <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                {[displayFirstName, displayLastName].filter(Boolean).join(" ")}
                {displayReg ? ` · ${displayReg}` : ""}
              </SoftTypography>
            </SoftBox>
            <SoftTypography variant="caption" sx={{ display: "block", mt: 1, color: figmaTokens.textMuted, maxWidth: 640 }}>
              {t("home.modal.hint", "Punch times, GPS distance to establishment, and device IMEI.")}
            </SoftTypography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 10,
                top: 14,
                color: figmaTokens.textSecondary,
                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 2, pt: 1 }}>
          <SoftBox
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              mb: 2.5,
            }}
          >
            <SoftBox sx={homeStatWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("home.modal.stats.records", "Entries") }}
                count={modalStats.count}
                percentage={{ color: "success", text: "" }}
                icon={{ color: "info", component: "history" }}
              />
            </SoftBox>
            <SoftBox sx={homeStatWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("home.modal.stats.avg_distance", "Avg. distance") }}
                count={modalStats.avgKm !== null ? `${modalStats.avgKm.toFixed(2)} km` : "—"}
                percentage={{ color: "success", text: "" }}
                icon={{ color: "warning", component: "straighten" }}
              />
            </SoftBox>
            <SoftBox sx={homeStatWrapSx}>
              <Card
                sx={{
                  flex: 1,
                  minHeight: 108,
                  borderRadius: "16px",
                  border: `1px solid ${figmaTokens.borderSubtle}`,
                  boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
                  backgroundColor: figmaTokens.surface,
                  backgroundImage: `linear-gradient(145deg, rgba(118, 75, 162, 0.08) 0%, ${figmaTokens.surface} 52%)`,
                  p: 2,
                  height: "100%",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <SoftBox
                  sx={{
                    width: "3rem",
                    height: "3rem",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: figmaTokens.gradientCSS,
                    color: "#fff",
                    flexShrink: 0,
                    boxShadow: "0 8px 20px rgba(102, 126, 234, 0.35)",
                  }}
                >
                  <Icon fontSize="small">date_range</Icon>
                </SoftBox>
                <SoftBox sx={{ minWidth: 0, flex: 1 }}>
                  <SoftTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: figmaTokens.textMuted,
                      mb: 0.5,
                    }}
                  >
                    {t("home.modal.stats.span", "First → last")}
                  </SoftTypography>
                  <SoftTypography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: figmaTokens.textPrimary,
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    {modalStats.spanRangeShort}
                  </SoftTypography>
                  {modalStats.spanTimeHint ? (
                    <SoftTypography variant="caption" sx={{ display: "block", mt: 0.75, color: figmaTokens.textSecondary }}>
                      {modalStats.spanTimeHint}
                    </SoftTypography>
                  ) : null}
                </SoftBox>
              </Card>
            </SoftBox>
            <SoftBox sx={homeStatWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("home.modal.stats.far", "Far (> 1 km)") }}
                count={modalStats.farCount}
                percentage={{ color: "success", text: "" }}
                icon={{ color: "error", component: "pin_drop" }}
              />
            </SoftBox>
          </SoftBox>

          {!loading && teacherHistory && teacherHistory.length === 0 && (
            <SoftBox
              sx={{
                py: 4,
                px: 2,
                mb: 2,
                borderRadius: "12px",
                bgcolor: figmaTokens.pageBg,
                border: `1px dashed ${figmaTokens.borderSubtle}`,
                textAlign: "center",
              }}
            >
              <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                {t("home.modal.empty", "No attendance history for this teacher.")}
              </SoftTypography>
            </SoftBox>
          )}

          {teacherHistory && teacherHistory.length > 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: `1px solid ${figmaTokens.borderSubtle}`,
                overflow: "auto",
                maxHeight: { xs: "50vh", md: 400 },
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  ...directoryTableHeadStickyOverridesSx,
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    px: 2.25,
                    py: 1.45,
                    fontSize: "0.8125rem",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.modal.attend_date", "Date de pointage")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.modal.est_coords", "Coordonnées de l'établissement")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.modal.att_coords", "Coordonnées du pointage")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.modal.distance", "Distance")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("home.modal.imei", "IMEI du téléphone")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTeacherHistory.map((row) => {
                    const km = rowDistanceKm(row);
                    return (
                      <TableRow
                        key={row.id ?? row.Id ?? `${row.attendDateTime}-${row.imei}`}
                        hover
                        sx={{
                          "& .MuiTableCell-root": {
                            borderBottom: `1px solid ${figmaTokens.borderSubtle}`,
                            fontSize: "0.8125rem",
                          },
                          "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {(row.attendDateTime ?? row.AttendDateTime)?.replace("T", " ") ?? "—"}
                        </TableCell>
                        <TableCell sx={{ color: figmaTokens.textBody, maxWidth: 200 }}>
                          {row.estLatitude ?? row.EstLatitude} {row.estLongitude ?? row.EstLongitude}
                          <br />
                          <SoftTypography component="span" variant="caption" sx={{ color: figmaTokens.textMuted }}>
                            ({row.estName ?? row.EstName ?? "—"})
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          <Link
                            to="/map"
                            state={{ row }}
                            onClick={() => localStorage.setItem("mapData", JSON.stringify(row))}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: figmaTokens.accent, fontWeight: 600 }}
                          >
                            {row.attLatitude ?? row.AttLatitude} {row.attLongitude ?? row.AttLongitude}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{km !== null ? `${km.toFixed(2)} km` : "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{row.imei ?? row.Imei ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {teacherHistory && teacherHistory.length > rowsPerPage && (
            <Stack spacing={2} alignItems="center" sx={{ pt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
            bgcolor: figmaTokens.pageBg,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.5,
              borderColor: figmaTokens.borderStrong,
              color: figmaTokens.textSecondary,
            }}
          >
            {t("home.modal.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

TeacherModal.propTypes = {
  openModel: PropTypes.bool.isRequired,
  setOpenModel: PropTypes.func.isRequired,
  teacherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Home;
